import {
  AllActions,
  AllCrudActions,
  EPermissionActions,
} from "./EPermissionActions";
import {
  EPermissionResources as EPermissionResourcesObj,
  type EPermissionResources as EPermissionResourcesType,
} from "./EPermissionResources";
import { capitalize } from "../strings/capitalize";

export const ECompanyRoleCode = {
  CompanyAdmin: "company_admin",
  PayrollAdmin: "payroll_admin",
  HRAdmin: "hr_admin",
  FinanceAdmin: "finance_admin",
  ReportsViewer: "reports_viewer",
  L1SupportAgent: "l1_support_agent",
  L2SupportSpecialist: "l2_support_specialist",
  L3SupportAdmin: "l3_support_admin",
} as const;

export type ECompanyRoleCode =
  (typeof ECompanyRoleCode)[keyof typeof ECompanyRoleCode];

/** Short product labels for support tiers (shell / tables); avoids "L3 Support Admin" style copy. */
const SHELL_DISPLAY_LABEL_BY_ROLE_CODE: Readonly<
  Partial<Record<ECompanyRoleCode, string>>
> = {
  [ECompanyRoleCode.L1SupportAgent]: "L1 Support",
  [ECompanyRoleCode.L2SupportSpecialist]: "L2 Support",
  [ECompanyRoleCode.L3SupportAdmin]: "L3 Support",
};

/**
 * Human-readable label for one company-member role code. Used when joining **all** roles for
 * shell/API copy — callers should not take "first role only".
 */
export const formatCompanyMemberRoleCodeForShellDisplay = (
  code: string
): string => {
  const mapped = SHELL_DISPLAY_LABEL_BY_ROLE_CODE[code as ECompanyRoleCode];
  if (mapped != null && mapped !== "") {
    return mapped;
  }
  return code
    .split("_")
    .map((part) => capitalize(String(part)))
    .join(" ");
};

/**
 * Joins every distinct role code for display (e.g. "Finance Admin · HR Admin", "L3 Support").
 */
export const formatCompanyMemberRoleCodesForShellDisplay = (
  codes: readonly string[]
): string =>
  [...new Set(codes.map((c) => String(c).trim()).filter((c) => c !== ""))]
    .map(formatCompanyMemberRoleCodeForShellDisplay)
    .join(" · ");

export type TRbacRolePermissions = Partial<
  Record<EPermissionResourcesType, number>
>;

/**
 * Static role-definition map for RBAC V1. Includes Company Admin, Payroll Admin, HR Admin,
 * Finance Admin, Reports Viewer, and Support tiers (L1/L2/L3). Used by permission engine
 * to resolve effective permissions from role assignments.
 */
export const RBAC_ROLE_PERMISSIONS: Record<
  ECompanyRoleCode,
  TRbacRolePermissions
> = {
  [ECompanyRoleCode.CompanyAdmin]: {
    [EPermissionResourcesObj.AccrualBalances]: AllActions,
    [EPermissionResourcesObj.AccrualPolicies]: AllActions,
    [EPermissionResourcesObj.CompanyMembers]: AllActions,
    [EPermissionResourcesObj.CompanySettings]: AllActions,
    [EPermissionResourcesObj.ContractorPayments]: AllActions,
    [EPermissionResourcesObj.Contractors]: AllActions,
    [EPermissionResourcesObj.Deductions]: AllActions,
    [EPermissionResourcesObj.Employees]: AllActions,
    [EPermissionResourcesObj.EmployeeChecks]: AllActions,
    [EPermissionResourcesObj.InviteMembers]: AllActions,
    [EPermissionResourcesObj.Locations]: AllActions,
    [EPermissionResourcesObj.Onboarding]: AllActions,
    [EPermissionResourcesObj.PartnerMembership]: AllActions,
    [EPermissionResourcesObj.Payroll]: AllActions,
    [EPermissionResourcesObj.Reports]: AllActions,
    [EPermissionResourcesObj.ShareableLinks]: AllCrudActions,
    [EPermissionResourcesObj.Shifts]: AllActions,
    [EPermissionResourcesObj.Taxes]: AllActions,
    [EPermissionResourcesObj.WorkerSupport]: AllCrudActions,
  },
  [ECompanyRoleCode.PayrollAdmin]: {
    [EPermissionResourcesObj.ContractorPayments]: AllActions,
    [EPermissionResourcesObj.Contractors]: EPermissionActions.Read,
    [EPermissionResourcesObj.Employees]: EPermissionActions.Read,
    [EPermissionResourcesObj.Payroll]: AllActions,
    [EPermissionResourcesObj.Reports]: EPermissionActions.Read,
    [EPermissionResourcesObj.ShareableLinks]: AllCrudActions,
    [EPermissionResourcesObj.Taxes]: EPermissionActions.Read,
    [EPermissionResourcesObj.WorkerSupport]: AllCrudActions,
  },
  [ECompanyRoleCode.HRAdmin]: {
    [EPermissionResourcesObj.Contractors]: AllCrudActions,
    [EPermissionResourcesObj.Employees]: AllCrudActions,
    [EPermissionResourcesObj.Onboarding]: AllActions,
    [EPermissionResourcesObj.ShareableLinks]: AllCrudActions,
    [EPermissionResourcesObj.WorkerSupport]: AllCrudActions,
  },
  [ECompanyRoleCode.FinanceAdmin]: {
    [EPermissionResourcesObj.Contractors]: EPermissionActions.Read,
    [EPermissionResourcesObj.Employees]: EPermissionActions.Read,
    [EPermissionResourcesObj.Reports]: EPermissionActions.Read,
    [EPermissionResourcesObj.Taxes]: EPermissionActions.Read,
  },
  /**
   * Reports Viewer is read-focused, but per product they MUST retain access to
   * "worker support / link generation" affordances (e.g. asking a worker to update
   * info via shareable link). This is a deliberate carve-out from the otherwise
   * view-only profile.
   */
  [ECompanyRoleCode.ReportsViewer]: {
    [EPermissionResourcesObj.Reports]: EPermissionActions.Read,
    [EPermissionResourcesObj.ShareableLinks]: AllCrudActions,
    [EPermissionResourcesObj.WorkerSupport]: AllCrudActions,
  },
  /**
   * Support tiers: previously `{}`, which made the team “Role-based access” table show only
   * `(access level)` and made {@link getEffectivePermissions} grant no RBAC bits for these roles.
   * Graduated access: L1 worker-support scope; L2 adds read across payroll/taxes/payments context;
   * L3 adds editor-level worker/contractor records plus read team list for escalations.
   * `ShareableLinks` is granted alongside `WorkerSupport` because the shareable-link generator
   * routes (`postGenerateShareableLink`, `postGeneratePaystubLink`) gate on `ShareableLinks:Create`.
   */
  [ECompanyRoleCode.L1SupportAgent]: {
    [EPermissionResourcesObj.WorkerSupport]: AllCrudActions,
    [EPermissionResourcesObj.ShareableLinks]: AllCrudActions,
    [EPermissionResourcesObj.Employees]: EPermissionActions.Read,
    [EPermissionResourcesObj.Contractors]: EPermissionActions.Read,
  },
  [ECompanyRoleCode.L2SupportSpecialist]: {
    [EPermissionResourcesObj.WorkerSupport]: AllCrudActions,
    [EPermissionResourcesObj.ShareableLinks]: AllCrudActions,
    [EPermissionResourcesObj.Employees]: EPermissionActions.Read,
    [EPermissionResourcesObj.Contractors]: EPermissionActions.Read,
    [EPermissionResourcesObj.Reports]: EPermissionActions.Read,
    [EPermissionResourcesObj.Payroll]: EPermissionActions.Read,
    [EPermissionResourcesObj.Taxes]: EPermissionActions.Read,
    [EPermissionResourcesObj.DirectPayments]: EPermissionActions.Read,
    [EPermissionResourcesObj.ContractorPayments]: EPermissionActions.Read,
    [EPermissionResourcesObj.EmbeddedHR]: EPermissionActions.Read,
    [EPermissionResourcesObj.CompanyName]: EPermissionActions.Read,
  },
  [ECompanyRoleCode.L3SupportAdmin]: {
    [EPermissionResourcesObj.WorkerSupport]: AllCrudActions,
    [EPermissionResourcesObj.ShareableLinks]: AllCrudActions,
    [EPermissionResourcesObj.Employees]: AllCrudActions,
    [EPermissionResourcesObj.Contractors]: AllCrudActions,
    [EPermissionResourcesObj.Reports]: EPermissionActions.Read,
    [EPermissionResourcesObj.Payroll]: EPermissionActions.Read,
    [EPermissionResourcesObj.Taxes]: EPermissionActions.Read,
    [EPermissionResourcesObj.DirectPayments]: EPermissionActions.Read,
    [EPermissionResourcesObj.ContractorPayments]: EPermissionActions.Read,
    [EPermissionResourcesObj.EmbeddedHR]: EPermissionActions.Read,
    [EPermissionResourcesObj.CompanyName]: EPermissionActions.Read,
    [EPermissionResourcesObj.CompanySettings]: EPermissionActions.Read,
    [EPermissionResourcesObj.CompanyMembers]: EPermissionActions.Read,
  },
};
